import { Ellipsis } from "lucide-react";
import {
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useMediaQuery } from "../../hooks";
import { useTranslation } from "../../i18n";
import { cn } from "../../utils";
import { IconButton } from "../Button/IconButton";
import { Select } from "../Select";
import type { SelectOption } from "../Select/types";
import { Tag } from "../Tag";
import type { TabsProps } from "./types";
import { useTabsIndicator } from "./useTabsIndicator";
import { useTabsOverflow } from "./useTabsOverflow";
import { useTabsScroll } from "./useTabsScroll";

export const Tabs = ({
  items,
  onChange,
  activeItem,
  className,
  variant = "hug",
}: TabsProps) => {
  const { rtl, t } = useTranslation();
  const isMobile = useMediaQuery("(max-width: 639px)");

  const tabButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const indicatorRef = useRef<HTMLDivElement | null>(null);
  const underlineRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const activeIndex = useMemo(() => {
    if (!activeItem) return -1;
    return items.findIndex((t) => t.id === activeItem);
  }, [items, activeItem]);

  const { lastVisibleIndex, showMore, hasCalculated } = useTabsOverflow({
    items,
    tabButtonRefs,
    scrollRef,
    enabled: !isMobile,
  });

  const isActiveTabHidden =
    !isMobile && hasCalculated && activeIndex > lastVisibleIndex;

  useTabsIndicator({
    items,
    activeIndex,
    rtl,
    tabButtonRefs,
    isActiveTabHidden,
    indicatorRef,
    underlineRef,
    scrollRef,
  });

  useTabsScroll({
    items,
    activeIndex,
    rtl,
    tabButtonRefs,
    enabled: isMobile,
    scrollRef,
  });

  const getTabVisibility = useCallback(
    (index: number) => {
      const shouldHideInitially = !isMobile && !hasCalculated;
      const isOverflowHidden =
        !isMobile && hasCalculated && index > lastVisibleIndex;
      return {
        shouldHideInitially,
        isOverflowHidden,
        isHidden: shouldHideInitially || isOverflowHidden,
      };
    },
    [isMobile, hasCalculated, lastVisibleIndex],
  );

  const handleButtonClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const nextId = event.currentTarget.getAttribute("data-tab-id");
      const isDisabled =
        event.currentTarget.getAttribute("aria-disabled") === "true";
      if (!nextId || isDisabled) return;
      onChange(nextId);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      // Filter to enabled AND visible tabs
      const enabledAndVisible = items.filter((item, index) => {
        const isDisabled = item.disabled;
        const { isHidden } = getTabVisibility(index);
        return !isDisabled && !isHidden;
      });

      if (enabledAndVisible.length === 0) return;

      const focused = document.activeElement as HTMLElement | null;
      const focusedId = focused?.getAttribute?.("data-tab-id");
      if (!focusedId) return;

      const currentIndex = enabledAndVisible.findIndex(
        (i) => i.id === focusedId,
      );
      if (currentIndex < 0) return;

      const moveFocus = (nextIndex: number) => {
        const nextItem = enabledAndVisible[nextIndex];
        if (!nextItem) return;
        const btn = tabButtonRefs.current.get(nextItem.id);
        btn?.focus();
      };

      switch (event.key) {
        case "ArrowLeft": {
          event.preventDefault();
          const next = rtl
            ? (currentIndex + 1) % enabledAndVisible.length
            : (currentIndex - 1 + enabledAndVisible.length) %
              enabledAndVisible.length;
          moveFocus(next);
          break;
        }
        case "ArrowRight": {
          event.preventDefault();
          const next = rtl
            ? (currentIndex - 1 + enabledAndVisible.length) %
              enabledAndVisible.length
            : (currentIndex + 1) % enabledAndVisible.length;
          moveFocus(next);
          break;
        }
        case "Home":
          event.preventDefault();
          moveFocus(0);
          break;
        case "End":
          event.preventDefault();
          moveFocus(enabledAndVisible.length - 1);
          break;
        case "Enter":
        case " ": {
          event.preventDefault();
          const btn = tabButtonRefs.current.get(focusedId);
          const nextId = btn?.getAttribute("data-tab-id");
          const isDisabled = btn?.getAttribute("aria-disabled") === "true";
          if (nextId && !isDisabled) onChange(nextId);
          break;
        }
        default:
          break;
      }
    },
    [items, onChange, rtl, isMobile, hasCalculated, lastVisibleIndex],
  );

  const hiddenTabOptions = useMemo((): SelectOption[] => {
    if (isMobile || !hasCalculated) return [];

    return items
      .filter((_, index) => index > lastVisibleIndex)
      .map(
        (item): SelectOption => ({
          id: item.id,
          text: item.text,
          disabled: item.disabled,
          className: "text-md",
        }),
      );
  }, [items, lastVisibleIndex, isMobile, hasCalculated]);

  const handleMoreTabSelect = useCallback(
    (selectedValue: string | undefined) => {
      if (selectedValue) {
        onChange(selectedValue);
      }
    },
    [onChange],
  );

  const renderMoreAnchor = useCallback(
    () => (
      <IconButton
        variant="ghost"
        aria-label={t("tabs.moreTabs")}
        style={{ outlineOffset: -2 }}
      >
        <Ellipsis />
      </IconButton>
    ),
    [t],
  );

  const itemsElements = useMemo(() => {
    const baseButton = cn(
      "py-2 px-4 rounded-lg whitespace-nowrap flex items-center gap-1",
      "focus-visible:outline-2 focus-visible:outline-primary-500 focus-visible:outline-offset-[-2px]",
      "transition-colors duration-200 motion-reduce:transition-none",
      variant === "fill" && "flex-1 justify-center",
    );

    const getTabIndex = (
      isActive: boolean,
      isOverflowHidden: boolean,
      index: number,
    ): number => {
      if (isActive && !isOverflowHidden) return 0;
      if (isActiveTabHidden && index === lastVisibleIndex) return 0;
      return -1;
    };

    return items.map((item, index) => {
      const isActive = item.id === activeItem;
      const { isOverflowHidden, isHidden } = getTabVisibility(index);
      const idAttr = `tab-${item.id}`;
      const btnClass = cn(
        baseButton,
        !item.disabled &&
          "hover:bg-primary-500/5 focus-visible:bg-primary-500/5 active:bg-primary-500/10",
        item.disabled && "text-neutral-300 cursor-not-allowed",
        isHidden && "opacity-0 pointer-events-none",
        item.className,
      );

      return (
        <button
          {...(item.dataAttributes || {})}
          key={item.id}
          id={idAttr}
          type="button"
          ref={(el) => {
            if (el) tabButtonRefs.current.set(item.id, el);
            else tabButtonRefs.current.delete(item.id);
          }}
          role="tab"
          className={btnClass}
          data-tab-id={item.id}
          aria-selected={isActive}
          aria-controls={`panel-${item.id}`}
          aria-disabled={item.disabled ? "true" : "false"}
          tabIndex={getTabIndex(isActive, isOverflowHidden, index)}
          onClick={handleButtonClick}
        >
          {item.text}
          {item.tagProps && <Tag {...item.tagProps} />}
        </button>
      );
    });
  }, [
    items,
    activeItem,
    handleButtonClick,
    getTabVisibility,
    isActiveTabHidden,
    lastVisibleIndex,
  ]);

  const rootClass = cn("flex flex-col gap-4", className);
  const scrollClass = cn(
    "relative flex items-center gap-1",
    isMobile ? "overflow-x-auto" : "overflow-x-hidden",
    "overflow-y-hidden",
    "[&::-webkit-scrollbar]:hidden",
    "[-ms-overflow-style:none]",
    "[scrollbar-width:none]",
    "pb-1",
    variant === "fill" && "w-full",
  );

  const content =
    activeIndex >= 0 && items[activeIndex]?.content
      ? items[activeIndex].content
      : null;

  return (
    <div className={rootClass}>
      <div
        ref={scrollRef}
        role="tablist"
        aria-orientation="horizontal"
        className={scrollClass}
        onKeyDown={handleKeyDown}
      >
        {itemsElements}

        {showMore && (
          <div className="absolute end-0">
            <Select
              options={hiddenTabOptions}
              value={isActiveTabHidden ? activeItem : undefined}
              onChange={handleMoreTabSelect}
              onRenderAnchor={renderMoreAnchor}
              placeholder=""
              placement="bottom-end"
              minWidth={200}
            />
          </div>
        )}

        <div
          ref={underlineRef}
          aria-hidden="true"
          className={cn(
            "absolute bottom-0 start-0 h-[2px] bg-neutral-500/10",
            "pointer-events-none",
          )}
        />

        <div
          ref={indicatorRef}
          aria-hidden="true"
          className={cn(
            "absolute bottom-0 start-0 h-[2px] w-0 rounded-[2px] bg-primary-500",
            "will-change-[transform,width]",
            "[&[data-initialized]]:transition-[transform,width,opacity] [&[data-initialized]]:duration-300",
            "motion-reduce:transition-none",
          )}
        />
      </div>

      {content && (
        <div
          id={`panel-${items[activeIndex].id}`}
          role="tabpanel"
          aria-labelledby={`tab-${items[activeIndex].id}`}
        >
          {content}
        </div>
      )}
    </div>
  );
};
